import "react-image-crop/dist/ReactCrop.css";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { centerCrop, Crop, makeAspectCrop, PercentCrop, PixelCrop, ReactCrop } from "react-image-crop";
import { Box, Button, Grid, Group, Image, LoadingOverlay, Modal, Paper, Slider, Text, Title } from "@mantine/core";
import { handleError } from "src/utils/error";

import { FormItem } from "../../components/FormItem";
import { createModal } from "../../hooks/modals";
import { useDebounce } from "../../hooks/utils";

const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
        makeAspectCrop(
            {
                unit: "%",
                width: 100,
            },
            aspect,
            mediaWidth,
            mediaHeight
        ),
        mediaWidth,
        mediaHeight
    );
};

export const drawImageToCanvas = async (
    image: HTMLImageElement,
    canvas: HTMLCanvasElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0
) => {
    const context = canvas.getContext("2d");

    if (!context) {
        throw new Error("No 2d context");
    }

    const scaleX = image?.naturalWidth / image.clientWidth;
    const scaleY = image?.naturalHeight / image.clientHeight;
    // devicePixelRatio slightly increases sharpness on retina devices
    // at the expense of slightly slower render times and needing to
    // size the image back down if you want to download/upload and be
    // true to the image's natural size.
    // const pixelRatio = window.devicePixelRatio;
    const pixelRatio = 1;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    context.scale(pixelRatio, pixelRatio);
    context.imageSmoothingQuality = "high";

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    const rotateRadians = (rotate * Math.PI) / 180;
    const centerX = image?.naturalWidth / 2;
    const centerY = image?.naturalHeight / 2;

    context.save();

    // 5) Move the crop origin to the canvas origin (0,0)
    context.translate(-cropX, -cropY);
    // 4) Move the origin to the center of the original position
    context.translate(centerX, centerY);
    // 3) Rotate around the origin
    context.rotate(rotateRadians);
    // 2) Scale the image
    context.scale(scale, scale);
    // 1) Move the center of the image to the origin (0,0)
    context.translate(-centerX, -centerY);
    context.drawImage(
        image,
        0,
        0,
        image?.naturalWidth,
        image?.naturalHeight,
        0,
        0,
        image?.naturalWidth,
        image?.naturalHeight
    );

    context.restore();
};

export interface CropImageModalProps {
    title?: string;
    imageSrc: string;
    minWidth?: number;
    minHeight?: number;
    aspectRatio?: {
        value?: number;
        label?: string;
    };
    onSave?(file: File);
}

export const CropImageModal = createModal<CropImageModalProps>(
    ({ title, imageSrc, minWidth, minHeight, aspectRatio, onSave, onClose }) => {
        const { t } = useTranslation(["common"]);

        const [isLoadingImage, setIsLoadingImage] = useState(true);
        const [isCroppingImage, setIsCroppingImage] = useState(false);

        const imageRef = useRef<HTMLImageElement>();
        const canvasRef = useRef<HTMLCanvasElement>();

        const [scale, setScale] = useState(1);
        const [rotation, setRotation] = useState(0);
        const [crop, setCrop] = useState<Crop>();
        const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

        const initCrop = useCallback(() => {
            setCrop(
                centerAspectCrop(imageRef?.current?.clientWidth, imageRef?.current?.clientHeight, aspectRatio.value)
            );
        }, [aspectRatio?.value]);

        useEffect(() => {
            if (crop && (!crop?.width || !crop?.height)) {
                initCrop();
            }
        }, [crop, crop?.width, crop?.height, initCrop]);

        useDebounce(
            () => {
                if (completedCrop?.width && completedCrop?.height && imageRef.current && canvasRef.current) {
                    drawImageToCanvas(imageRef.current, canvasRef.current, completedCrop, scale, rotation);
                }
            },
            75,
            [completedCrop, scale, rotation]
        );

        const handleImageLoaded = useCallback(() => {
            setIsLoadingImage(false);

            if (aspectRatio?.value) {
                initCrop();
            }
        }, [aspectRatio?.value, initCrop]);

        const handleChange = useCallback((pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
            setCrop(percentCrop);
        }, []);

        const handleComplete = useCallback((pixelCrop: PixelCrop) => {
            setCompletedCrop(pixelCrop);
        }, []);

        const handleSave = useCallback(async () => {
            setIsCroppingImage(true);

            try {
                const image = imageRef.current;
                const canvas = canvasRef.current;

                if (!image || !canvas || !completedCrop) {
                    throw new Error("Crop canvas does not exist");
                }

                const scaleX = image?.naturalWidth / image.clientWidth;
                const scaleY = image?.naturalHeight / image.clientHeight;

                const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);

                const context = offscreen.getContext("2d");

                if (!context) {
                    throw new Error("No 2D context");
                }

                context.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, offscreen.width, offscreen.height);

                const blob = await offscreen.convertToBlob({ type: "image/png" });
                const file = new File([blob], `${Math.random().toString(36).substring(2)}.png`);

                onSave?.(file);
                onClose?.();
            } catch (err) {
                handleError(err, { context: { imageSrc } });
            } finally {
                setIsCroppingImage(false);
            }
        }, [completedCrop, imageSrc, onClose, onSave]);

        return (
            <Modal size="xl" title={<Title order={3}>{title || t("image_edit")}</Title>} onClose={onClose} opened>
                <LoadingOverlay visible={isLoadingImage || isCroppingImage} />
                <Grid mb="md">
                    <Grid.Col xs={12} sm={6}>
                        <FormItem label={t("image_scale")}>
                            <Group spacing="xs" align="center">
                                <Text w="42px" align="right" weight={500}>
                                    {scale} x
                                </Text>
                                <Box style={{ flex: 1 }}>
                                    <Slider
                                        color="orange"
                                        min={0.1}
                                        max={10}
                                        step={0.1}
                                        value={scale}
                                        onChange={(scale) => setScale(scale)}
                                    />
                                </Box>
                            </Group>
                        </FormItem>
                    </Grid.Col>
                    <Grid.Col xs={12} sm={6}>
                        <FormItem label={t("image_rotation")}>
                            <Group spacing="xs" align="center">
                                <Text w="42px" align="right" weight={500}>
                                    {rotation} °
                                </Text>
                                <Box style={{ flex: 1 }}>
                                    <Slider
                                        color="orange"
                                        min={-180}
                                        max={180}
                                        step={1}
                                        value={rotation}
                                        onChange={(value) => setRotation(Math.min(180, Math.max(-180, value)))}
                                    />
                                </Box>
                            </Group>
                        </FormItem>
                    </Grid.Col>
                </Grid>
                <Paper shadow="xs" style={{ overflow: "hidden" }} withBorder>
                    <ReactCrop
                        minWidth={minWidth}
                        minHeight={minHeight}
                        crop={crop}
                        aspect={aspectRatio?.value}
                        style={{ display: "block" }}
                        onChange={handleChange}
                        onComplete={handleComplete}
                    >
                        <Image
                            imageRef={imageRef}
                            src={imageSrc}
                            style={{ transform: `scale(${scale}) rotate(${rotation}deg)` }}
                            onLoad={handleImageLoaded}
                        />
                    </ReactCrop>
                </Paper>
                <canvas
                    ref={canvasRef}
                    style={{
                        display: "none",
                        objectFit: "contain",
                        width: completedCrop?.width,
                        height: completedCrop?.height,
                    }}
                />
                <Group mt="lg" position="right">
                    <Button variant="subtle" onClick={onClose}>
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleSave}>{t("image_save")}</Button>
                </Group>
            </Modal>
        );
    }
);
